import React, { useState } from 'react';

import {
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { KingdomSearchResult, Province } from '../../@types/KingdomResponse';
import { useIntelState } from '../../context/IntelState';
import RestUtilities from '../../services/RestUtilities';

const KdSearch: React.FC = () => {
  const { intelState } = useIntelState();
  const [dataSet, setDataSet] = useState<KingdomSearchResult[] | undefined>(
    undefined
  );
  const [fetching, setFetching] = useState(false);
  const [acres, setAcres] = useState(
    intelState.ownData?.provinces.reduce(
      (a: number, b: Province) => a + b.land,
      0
    )
  );
  const [networth, setNetworth] = useState(
    intelState.ownData?.provinces.reduce(
      (a: number, b: Province) => a + b.networth,
      0
    )
  );
  const [provinces, setProvinces] = useState(
    intelState.ownData?.provinces.length ?? 0
  );

  const refreshData = () => {
    const getData = async () => {
      const response = await RestUtilities.post(`Kingdom/v1/Search`, {
        server: intelState.server,
        acres: acres,
        networth: networth,
        provinces: provinces,
      });
      if (response.ok) {
        const results: KingdomSearchResult[] = response.content;
        setDataSet(results);
        setFetching(false);
      }
    };
    if (!fetching) {
      setFetching(true);
      getData();
    }
  };

  return (
    <Box my={2}>
      <Typography variant='h4' component='div'>
        Kingdom Search
      </Typography>
      <Typography variant='body1' component='div'>
        The search will return results within +/- 30% of the selected
        acres/networth values and within +/- 3 provinces count.
      </Typography>
      <Typography variant='body1' component='div'>
        Currently we have a bug where normal status KDs are not being saved
        correctly, so results only include eowcf and war, Check back in a few
        days.
      </Typography>
      <Grid container spacing={2} mt={2} columns={8}>
        <Grid item>
          <TextField
            fullWidth
            size='small'
            type='number'
            label='Acres'
            sx={{ minWidth: '100px' }}
            InputProps={{ inputProps: { min: 1, max: 1000000 } }}
            value={acres}
            onChange={(e) => setAcres(Number(e.target.value))}
          />
        </Grid>
        <Grid item>
          <TextField
            fullWidth
            size='small'
            type='number'
            label='Networth'
            sx={{ minWidth: '130px' }}
            InputProps={{ inputProps: { min: 1, max: 1000000 } }}
            value={networth}
            onChange={(e) => setNetworth(Number(e.target.value))}
          />
        </Grid>
        <Grid item>
          <TextField
            fullWidth
            size='small'
            type='number'
            label='Provinces'
            sx={{ minWidth: '100px' }}
            InputProps={{ inputProps: { min: 1, max: 25 } }}
            value={provinces}
            onChange={(e) => setProvinces(Number(e.target.value))}
          />
        </Grid>
        <Grid item>
          <Button variant='outlined' onClick={refreshData}>
            Search
          </Button>
        </Grid>
      </Grid>
      {dataSet?.length && (
        <TableContainer component={Paper} sx={{ my: 2 }}>
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Location</TableCell>
                <TableCell>Kingdom Name</TableCell>
                <TableCell align='left'>Status</TableCell>
                <TableCell align='left'>War Location</TableCell>
                <TableCell align='right'>Acres</TableCell>
                <TableCell align='right'>Networth</TableCell>
                <TableCell align='right'>Provinces</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataSet.map((row: KingdomSearchResult) => (
                <TableRow
                  key={row.location}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component='th' scope='row'>
                    {row.location}
                  </TableCell>
                  <TableCell>{row.kingdomName}</TableCell>
                  <TableCell>{row.warStatus}</TableCell>
                  <TableCell>{row.warLocation}</TableCell>
                  <TableCell align='right'>{row.land}</TableCell>
                  <TableCell align='right'>{row.netWorth}</TableCell>
                  <TableCell align='right'>{row.provinces}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default KdSearch;
